import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Typography,
  BackArrow,
  InfoBlock,
  ModalVariables
} from '../../../components';
import DropOptions from '../../../components/drop-options';
import { PropsContainer } from '../../../components/table/styles';
import affiliateService from '../../../api/affiliateService';
import { icons } from '../../../icons';
import { useTranslation } from 'react-i18next';
import { useUi } from '../../../context/ui.context';
import { usePageTitle } from '../../../context/navigationContext';
import { timeConverterMMDDYYHHMM } from '../../../utils/dates';

// Types for the affiliate settings
type AffiliateSettings = {
  id: string;
  name: string;
  description: string;
  is_default: boolean;
  is_active: boolean;
  manual_referrals_accept: boolean;
  signup_bonus: boolean;
  signup_bonus_value: string;
  percent_payment: string;
  created_at: string;
  updated_at: string;
  signup_required_credit_card?: boolean;
  signup_required_days?: number | null;
  signup_minimal_payment?: number | null;
  percent_minimal_payment?: number | null;
};


const AffiliateSettingsView = () => {
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [setting, setSetting] = useState<AffiliateSettings | null>(null);
  const [options, setOptions] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [activeAnimation, setActiveAnimation] = useState(false);
  const [modalType, setModalType] = useState<string>('');
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertSubTitle, setAlertSubTitle] = useState<any>('');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const optionsRef = useRef<HTMLDivElement>(null);
  const dropsRef = useRef<HTMLDivElement>(null);
  const [t] = useTranslation();
  const { viewAlert } = useUi();
  usePageTitle(t("AFFILIATE_SETTINGS"));

  // Load setting data
  const loadSetting = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      let settingData;
      try {
        const response = await affiliateService.getSettingById(id);
        settingData = response.data;
      } catch {
        // Fallback to getting all settings and finding by ID
        const response = await affiliateService.getSettings();
        settingData = response.data.results.find((s: any) => s.id === id);
      }
      
      if (settingData) {
        setSetting(settingData);
      } else {
        viewAlert({
          severity: 'error',
          message: 'Setting not found.'
        });
      }
    } catch (error) {
      console.error('Error loading setting:', error);
      viewAlert({
        severity: 'error',
        message: 'Failed to load setting.'
      });
    } finally {
      setLoading(false);
    }
  };

  // Open delete modal
  const openDeleteModal = () => {
    if (!setting) return;
    setAlertTitle('Affiliate Setting');
    setAlertSubTitle([setting]);
    setModalType('delete');
    setViewModal(true);
    setActiveAnimation(true);
    setOptions(false); // Close dropdown menu
  };

  // Delete setting (called from modal)
  const handleDelete = async () => {
    if (!setting || !id) return;
    
    setDeleteLoading(true);
    try {
      await affiliateService.deleteSettings(id);
      viewAlert({
        severity: 'information',
        message: 'Setting deleted successfully!'
      });
      setTimeout(() => {
        navigate('/billing/referrals');
      }, 2000);
    } catch (error) {
      console.error('Error deleting setting:', error);
      viewAlert({
        severity: 'error',
        message: 'Failed to delete setting.'
      });
    } finally {
      setDeleteLoading(false);
      setViewModal(false);
      setActiveAnimation(false);
    }
  };

  // Close modal
  const closeModal = () => {
    if (modalType === 'delete') {
      handleDelete();
    }
  };

  const closeModalCancel = () => {
    setViewModal(false);
    setActiveAnimation(false);
  };

  useEffect(() => {
    loadSetting();
  }, [id]);


  // Close dropdown when clicking outside (like in instance page)
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (optionsRef.current && !optionsRef.current.contains(event.target) && 
          dropsRef.current && !dropsRef.current.contains(event.target)) {
        setOptions(false);
      }
    };

    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '20px' }}>
        <Typography variant="h1" customStyles={{ marginBottom: '20px' }}>
          Affiliate Settings
        </Typography>
        <div>Loading...</div>
      </div>
    );
  }

  if (!setting) {
    return (
      <div style={{ padding: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '30px' }}>
          <BackArrow fallbackPath="/billing/referrals" />
          <Typography variant="h1">
            Affiliate Settings
          </Typography>
        </div>
      </div>
    );
  }

  // Dropdown options (like in instance page)
  const getItemsDefault = () => {
    const items:any = [
      {
        title: 'Edit',
        id: 'edit',
        icon: icons.Edit,
        divider: false,
        onClick: () => {
          setOptions(false);
          navigate(`/billing/referrals/edit/${setting?.id}`);
        }
      }
    ];

    // Add Delete option only if setting is not default
    if (setting && !setting.is_default) {
      items.push({
        title: 'Delete',
        id: 'delete',
        icon: icons.DeleteIcon,
      onClick: () => {
        openDeleteModal();
      }
      });
    }

    return items;
  };

  // Format data for InfoBlock
  const formatInfoData = () => {
    const data = [
      {
        title: 'Basic Information',
        type: 'subtitle'
      },
      {
        title: 'Name',
        value: setting.name
      },
      {
        title: 'Description',
        value: setting.description
      },
      {
        title: 'Default Setting',
        value: setting.is_default ? 'Yes' : 'No'
      },
      {
        title: 'Active',
        value: setting.is_active ? 'Yes' : 'No'
      },
      {
        type: 'divider'
      },
      {
        title: 'Referral Settings',
        type: 'subtitle'
      },
      {
        title: 'Manual Referrals Accept',
        value: setting.manual_referrals_accept ? 'Yes' : 'No'
      },
      {
        type: 'divider'
      },
      {
        title: 'Signup Bonus Settings',
        type: 'subtitle'
      },
      {
        title: 'Signup Bonus Enabled',
        value: setting.signup_bonus ? 'Yes' : 'No'
      }
    ];

    if (setting.signup_bonus) {
      data.push({
        title: 'Signup Bonus Value',
        value: `$${setting.signup_bonus_value}`
      });
    }

    if (setting.signup_required_credit_card !== undefined) {
      data.push({
        title: 'Require Credit Card',
        value: setting.signup_required_credit_card ? 'Yes' : 'No'
      });
    }

    if (setting.signup_required_days) {
      data.push({
        title: 'Required Days',
        value: setting.signup_required_days.toString()
      });
    }

    if (setting.signup_minimal_payment) {
      data.push({
        title: 'Signup Minimal Payment',
        value: `$${setting.signup_minimal_payment}`
      });
    }

    data.push(
      {
        type: 'divider'
      },
      {
        title: 'Payment Settings',
        type: 'subtitle'
      },
      {
        title: 'Payment Percentage',
        value: `${setting.percent_payment}%`
      }
    );

    if (setting.percent_minimal_payment) {
      data.push({
        title: 'Percent Minimal Payment',
        value: `${setting.percent_minimal_payment}%`
      });
    }

    data.push(
      {
        type: 'divider'
      },
      {
        title: 'System Information',
        type: 'subtitle'
      },
      {
        title: 'Created At',
        value: timeConverterMMDDYYHHMM(setting.created_at)
      },
      {
        title: 'Updated At',
        value: timeConverterMMDDYYHHMM(setting.updated_at)
      },
      {
        title: 'Setting ID',
        value: setting.id
      }
    );

    return data;
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <BackArrow fallbackPath="/billing/referrals" />
          <Typography variant="h1">
            {setting.name}
          </Typography>
        </div>
        
        <div style={{ position: 'relative', display: 'flex', gap: '12px', alignItems: 'center' }}>
          <div ref={dropsRef}>
            <Button
              variant="stroke"
              size="small"
              icon={icons.More}
              title="Menu"
              onClick={() => setOptions(!options)}
            />
          </div>
          <PropsContainer style={{top:'35px', right:'0px'}} ref={optionsRef} active={options}>
            <DropOptions
              options={getItemsDefault()}
              handleClose={() => setOptions(false)}
            />
          </PropsContainer>
        </div>
      </div>


      <InfoBlock
        data={formatInfoData()}
        title="Affiliate Settings Details"
        customStyles={{ marginTop: '20px' }}
      />

      <ModalVariables
        modalType={modalType as any}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={closeModal}
        closeModalCancel={closeModalCancel}
        alertTitle={alertTitle}
        alertSubTitle={alertSubTitle}
        loading={deleteLoading}
      />
    </div>
  );
};

export default AffiliateSettingsView;
